import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InlineNotification, Tile } from "@carbon/react";
import StatCard from "../../components/carbon/StatCard/StatCard";
import DataTable, {
  type DataTableColumn,
} from "../../components/carbon/DataTable/DataTable";
import type { ImportPreview, ImportResolveType } from "../../types";
import UnresolvedValuesPanel from "./UnresolvedValuesPanel";
import { describeIssue, describeWarning } from "./importMessages";

type Option = { id: string; label: string };

type Props = {
  preview: ImportPreview;
  options: Record<ImportResolveType | "NONE", Option[]>;
  isResolving: boolean;
  onResolve: (resolutions: Record<string, Record<string, string>>) => void;
};

type ProvenanceRow = {
  id: string;
  field: ReactNode;
  mapped: number;
  defaulted: number;
  nulled: number;
  converted: number;
  unreadable: ReactNode;
};

const PROVENANCE_COLUMNS = [
  "field",
  "mapped",
  "defaulted",
  "nulled",
  "converted",
  "unreadable",
] as const;

/** A list inside a notification: the repeated lines, the lines imported before. */
const IdentityList = ({
  values,
  suffix,
}: {
  values: string[];
  suffix?: (index: number) => string;
}) => (
  <ul className="pr-import__notification-list">
    {values.map((value, index) => (
      <li key={value}>
        <code className="pr-import__code">{value}</code>
        {suffix ? ` ${suffix(index)}` : null}
      </li>
    ))}
  </ul>
);

/**
 * What would be created, and what we still need to be told.
 *
 * The section that earns this screen is the unresolved values (see `UnresolvedValuesPanel`).
 * Everything else here is the evidence the buyer checks the mapping against.
 */
const PreviewStep = ({ preview, options, isResolving, onResolve }: Props) => {
  const { t, i18n } = useTranslation();
  const exists = (key: string) => i18n.exists(key);

  const tiles: Array<[string, number]> = [
    ["sheets", preview.sheetCount],
    ["rowsRead", preview.rowsRead],
    ["rowsSkipped", preview.rowsSkipped],
    ["requests", preview.requestCount],
    ["groups", preview.groupCount],
    ["items", preview.itemCount],
    ["toDecide", preview.unresolvedValues.length],
  ];

  const inFileDuplicates = preview.inFileDuplicates.slice(0, 20);

  const provenanceHeaders: DataTableColumn<ProvenanceRow>[] =
    PROVENANCE_COLUMNS.map((key) => ({
      key,
      header: t(`purchaseRequests.import.table.${key}`),
    }));

  const provenanceRows: ProvenanceRow[] = preview.fields.map((field) => ({
    id: field.target,
    field: <code className="pr-import__code">{field.target}</code>,
    mapped: field.mapped,
    defaulted: field.defaulted,
    nulled: field.nulled,
    converted: field.coerced,
    unreadable:
      field.invalid > 0 ? (
        <span className="pr-import__row-error">{field.invalid}</span>
      ) : (
        field.invalid
      ),
  }));

  return (
    <>
      <div className="pr-import__stats">
        {tiles.map(([key, value]) => (
          <StatCard
            key={key}
            value={value}
            label={t(`purchaseRequests.import.tiles.${key}`)}
          />
        ))}
      </div>

      {inFileDuplicates.length > 0 && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          className="pr-import__notification"
          title={t("purchaseRequests.import.repeatedLines")}
          subtitle={t("purchaseRequests.import.repeatedLinesHint")}
        >
          <IdentityList
            values={inFileDuplicates.map(
              (duplicate) => duplicate.identityValue
            )}
            suffix={(index) =>
              t("purchaseRequests.import.rowsList", {
                rows: inFileDuplicates[index].rows.join(", "),
              })
            }
          />
        </InlineNotification>
      )}

      <UnresolvedValuesPanel
        values={preview.unresolvedValues}
        options={options}
        isResolving={isResolving}
        onResolve={onResolve}
      />

      {preview.historyDuplicates.length > 0 && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          className="pr-import__notification"
          title={t("purchaseRequests.import.alreadyImported")}
          subtitle={t("purchaseRequests.import.alreadyImportedHint")}
        >
          <IdentityList
            values={preview.historyDuplicates
              .slice(0, 20)
              .map((duplicate) => duplicate.identityValue)}
          />
        </InlineNotification>
      )}

      <Tile className="pr-import__panel pr-import__panel--flush">
        <div className="pr-import__panel-head">
          <h3 className="pr-import__title">
            {t("purchaseRequests.import.provenance")}
          </h3>
          <p className="pr-import__hint">
            {t("purchaseRequests.import.provenanceHint")}
          </p>
        </div>
        <DataTable
          headers={provenanceHeaders}
          rows={provenanceRows}
          isLoading={false}
          empty={
            <p className="pr-import__hint">
              {t("purchaseRequests.import.noFields")}
            </p>
          }
        />
      </Tile>

      {preview.coercions.length > 0 && (
        <Tile className="pr-import__panel">
          <h3 className="pr-import__title">
            {t("purchaseRequests.import.coercions")}
          </h3>
          <ul className="pr-import__plain-list">
            {preview.coercions.map((coercion) => (
              <li key={`${coercion.column}-${coercion.toType}`}>
                <strong>{coercion.column}</strong>{" "}
                <span className="pr-import__muted">
                  {/* A value can change without its type changing — EA becoming UN. Saying
                      "text to text" for that reads like a mistake rather than a translation. */}
                  {coercion.fromType === coercion.toType
                    ? t("purchaseRequests.import.normalised")
                    : t("purchaseRequests.import.convertedFromTo", {
                        from: coercion.fromType,
                        to: coercion.toType,
                      })}
                </span>{" "}
                <span className="pr-import__muted">({coercion.rowCount})</span>
              </li>
            ))}
          </ul>
        </Tile>
      )}

      {preview.warnings.length > 0 && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          className="pr-import__notification"
          title={t("purchaseRequests.import.warnings")}
        >
          <ul className="pr-import__notification-list">
            {preview.warnings.map((warning) => (
              <li key={`${warning.code}-${warning.subject}`}>
                {describeWarning(warning, t, exists)}
              </li>
            ))}
          </ul>
        </InlineNotification>
      )}

      {preview.issues.length > 0 && (
        <Tile className="pr-import__panel">
          <h3 className="pr-import__title">
            {t("purchaseRequests.import.rowIssues")}
          </h3>
          <ul className="pr-import__plain-list pr-import__plain-list--scroll">
            {preview.issues.slice(0, 50).map((issue, index) => (
              <li key={`${issue.rowNumber}-${issue.column}-${index}`}>
                <span
                  className={
                    issue.severity === "ERROR"
                      ? "pr-import__row-error"
                      : "pr-import__muted"
                  }
                >
                  {t("purchaseRequests.import.rowLabel", {
                    row: issue.rowNumber,
                  })}
                </span>{" "}
                {describeIssue(issue, t, exists)}
              </li>
            ))}
          </ul>
        </Tile>
      )}
    </>
  );
};

export default PreviewStep;
